import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { RULES } from "@/constants/testIds";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_GROUPS = [
  {
    label: "Welcome",
    items: [
      ["1.1-what-is-ucenter", "What is U.CENTER?"],
      ["1.2-why-ucenter-exists", "Why U.CENTER Exists"],
      ["1.3-the-vision", "The U.CENTER Vision"],
      ["1.4-who-its-for", "Who U.CENTER Is For"],
    ],
  },
  {
    label: "Membership",
    items: [
      ["4-membership", "Membership Overview"],
      ["4.1-packages", "Packages & Pricing"],
      ["4.2-shares", "Shares & Bonus Shares"],
    ],
  },
  {
    label: "Compensation Plan",
    items: [
      ["5-comp-plan", "Compensation Plan Overview"],
      ["5.1-four-bonus-systems", "Four Bonus Systems"],
      ["5.2-twenty-ranks", "The 20-Rank System"],
      ["5.3-three-bonuses-per-order", "Three Bonuses Per Order"],
      ["5.4-initial-ripple", "Initial Ripple"],
      ["5.5-ripple-wave", "Ripple Wave"],
      ["5.6-deep-ripple", "Deep Ripple"],
      ["5.7-drop-match", "Drop Match"],
    ],
  },
  {
    label: "Compliance & Reference",
    items: [
      ["7.2-disclaimers", "Disclaimers"],
      ["glossary", "Glossary"],
      ["faq", "FAQ"],
    ],
  },
];

const DEFAULT_SECTION = "5.1-four-bonus-systems";

export default function Rules() {
  const [sections, setSections] = useState(null);
  const [active, setActive] = useState(DEFAULT_SECTION);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/docs`)
      .then((r) => setSections(r.data.sections))
      .catch((e) => setErr(e.message));
  }, []);

  const content = useMemo(() => {
    if (!sections) return null;
    return sections[active] || "Section not available.";
  }, [sections, active]);

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 pt-10 pb-24" data-testid={RULES.container}>
      <div className="mb-8 fade-up">
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
          Rules reference
        </div>
        <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tight">
          The <span className="grad-text">compensation plan</span>, straight from the source.
        </h1>
        <p className="mt-3 text-slate-400 max-w-2xl">
          Content scraped from{" "}
          <a href="https://u.center/docs" target="_blank" rel="noreferrer" className="text-pink-400 hover:text-pink-300 underline decoration-dotted">u.center/docs</a>{" "}
          and served by the simulator&apos;s API so the numbers you see are always aligned with the published rules.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Sidebar (desktop) + Select (mobile) */}
        <aside className="lg:col-span-4 fade-up fade-up-1">
          {/* Mobile select */}
          <div className="lg:hidden mb-4">
            <Select value={active} onValueChange={setActive}>
              <SelectTrigger data-testid={RULES.sectionSelect} className="h-11 bg-white/[0.04] border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SECTION_GROUPS.map((g) => (
                  <div key={g.label} className="py-1">
                    <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-slate-500">{g.label}</div>
                    {g.items.map(([id, name]) => (
                      <SelectItem key={id} value={id}>{name}</SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Desktop list */}
          <nav className="hidden lg:block glass rounded-2xl p-3 sticky top-24">
            {SECTION_GROUPS.map((g) => (
              <div key={g.label} className="py-2">
                <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-slate-500">
                  {g.label}
                </div>
                <ul className="mt-1">
                  {g.items.map(([id, name]) => (
                    <li key={id}>
                      <button
                        onClick={() => setActive(id)}
                        className={`w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${
                          active === id
                            ? "text-white bg-white/[0.06]"
                            : "text-slate-400 hover:text-white hover:bg-white/[0.03]"
                        }`}
                      >
                        {name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <article className="lg:col-span-8 fade-up fade-up-2">
          <div className="glass rounded-2xl p-6 md:p-8 min-h-[400px]">
            {err && (
              <div className="text-red-400 text-sm">Failed to load: {err}</div>
            )}
            {!err && !content && (
              <div className="space-y-3">
                <Skeleton className="h-5 w-1/3 bg-white/[0.06]" />
                <Skeleton className="h-4 w-2/3 bg-white/[0.06]" />
                <Skeleton className="h-4 w-full bg-white/[0.06]" />
                <Skeleton className="h-4 w-5/6 bg-white/[0.06]" />
              </div>
            )}
            {content && <RenderedSection text={content} />}
          </div>
        </article>
      </div>
    </div>
  );
}

/**
 * Render the scraped section text with light structure detection.
 * Format signals from the scraper:
 *  - The first line is always "Back"
 *  - Second line is a breadcrumb (e.g., "Docs")
 *  - Third line is a section group (e.g., "Membership")
 *  - Fourth line is the title (e.g., "4.1 Packages & Pricing")
 *  - Content follows, tables use \t between cells.
 *  - "Previous"/"Next" navigation appears near the end.
 */
function RenderedSection({ text }) {
  const lines = text.split("\n");
  // strip "Back", breadcrumb, and Previous/Next tail
  const cleaned = [];
  let stopped = false;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (i === 0 && l.trim() === "Back") continue;
    if (l.trim() === "Previous" || l.trim() === "Next") {
      stopped = true;
      break;
    }
    cleaned.push(l);
  }
  // detect title (contains "." like 4.1 or a numbered heading)
  let title = "";
  const remaining = [...cleaned];
  // often first 2-3 lines are breadcrumbs
  while (remaining.length && (remaining[0].trim() === "Docs" || /^[A-Z][a-zA-Z & ]+$/.test(remaining[0].trim()) && remaining[0].length < 30 && !remaining[0].includes(".") && !title)) {
    remaining.shift();
  }
  if (remaining.length) {
    title = remaining.shift().trim();
  }

  // group content into paragraphs vs tables
  const blocks = [];
  let currentTable = null;
  for (const raw of remaining) {
    const l = raw.trimEnd();
    if (!l.trim()) {
      if (currentTable) { blocks.push({ type: "table", rows: currentTable }); currentTable = null; }
      continue;
    }
    if (l.includes("\t")) {
      const cells = l.split("\t").map((c) => c.trim());
      if (!currentTable) currentTable = [];
      currentTable.push(cells);
    } else {
      if (currentTable) { blocks.push({ type: "table", rows: currentTable }); currentTable = null; }
      // simple heuristic for headings
      if (/^[A-Z][^.!?]{2,60}$/.test(l.trim()) && l.length < 70 && !l.endsWith(".") && !l.endsWith(":")) {
        blocks.push({ type: "h3", text: l.trim() });
      } else {
        blocks.push({ type: "p", text: l });
      }
    }
  }
  if (currentTable) blocks.push({ type: "table", rows: currentTable });

  return (
    <div className="prose prose-invert max-w-none">
      {title && <h2 className="font-display text-2xl font-semibold tracking-tight text-white">{title}</h2>}
      <div className="mt-5 space-y-4" data-testid="rules-content">
        {blocks.map((b, i) => {
          if (b.type === "h3") return <h3 key={i} className="font-display text-lg text-white mt-6">{b.text}</h3>;
          if (b.type === "p") return <p key={i} className="text-slate-300 leading-relaxed text-[15px]">{b.text}</p>;
          if (b.type === "table") {
            const [header, ...rows] = b.rows;
            const looksLikeHeader = header && header.length > 1 && !/\$|%|^\d/.test(header[0]);
            const headerRow = looksLikeHeader ? header : null;
            const bodyRows = looksLikeHeader ? rows : b.rows;
            return (
              <div key={i} className="overflow-x-auto rounded-xl border border-white/[0.08]">
                <table className="w-full text-sm">
                  {headerRow && (
                    <thead>
                      <tr className="bg-white/[0.03]">
                        {headerRow.map((h, j) => (
                          <th key={j} className="px-4 py-2 text-left text-[11px] uppercase tracking-widest text-slate-400 font-medium">{h}</th>
                        ))}
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    {bodyRows.map((row, j) => (
                      <tr key={j} className="border-t border-white/[0.05]">
                        {row.map((c, k) => (
                          <td key={k} className={`px-4 py-2 ${k === 0 ? "text-white font-medium" : "text-slate-300"} font-mono text-[13px]`}>
                            {c}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}
